// src/utils/typeUtils.ts - FIXED VERSION

import {
  Player,
  PlayerStats,
  Team,
  League,
  Matchup,
  PlayerPosition,
  StadiumType,
  NFLTeam
} from '../types';
import { getStatsField, getTeamField, getLeagueField } from './fieldCompatibility';

// ===== TYPE GUARDS =====

export const isKicker = (player: Player): boolean => {
  return player.position === 'K';
};

export const isPunter = (player: Player): boolean => {
  return player.position === 'P';
};

export const isDomeTeam = (nflTeam: NFLTeam): boolean => {
  return (nflTeam.stadiumType || nflTeam.stadium_type) === 'dome';
};

export const isRetractableRoof = (nflTeam: NFLTeam): boolean => {
  return (nflTeam.stadiumType || nflTeam.stadium_type) === 'retractable';
};

export const isOutdoorStadium = (nflTeam: NFLTeam): boolean => {
  return (nflTeam.stadiumType || nflTeam.stadium_type) === 'outdoor';
};

export const isSeasonLongLeague = (league: League): boolean => {
  return getLeagueField.type(league) === 'season_long';
};

export const isWeeklyRedraftLeague = (league: League): boolean => {
  return getLeagueField.type(league) === 'weekly_redraft';
};

// ===== TEAM & ROSTER UTILITIES =====

export const getTeamWinPercentage = (team: Team): number => {
  const totalGames = team.wins + team.losses;
  if (totalGames === 0) return 0;
  return Math.round((team.wins / totalGames) * 1000) / 10; // Return as percentage with 1 decimal
};

export const getStartersByPosition = (roster: Player[], position: PlayerPosition): Player[] => {
  return roster.filter(player => player.position === position);
};

export const getRosterByPosition = (team: Team, position: PlayerPosition): Player[] => {
  if (!team.roster) return [];
  return team.roster
    .filter(slot => slot.player?.position === position)
    .map(slot => slot.player!)
    .filter(Boolean);
};

export const getAvailableRosterSlots = (team: Team): { K: number; P: number } => {
  if (!team.roster) return { K: 2, P: 2 };
  
  const kickerCount = team.roster.filter(slot => slot.player?.position === 'K').length;
  const punterCount = team.roster.filter(slot => slot.player?.position === 'P').length;
  
  return {
    K: Math.max(0, 2 - kickerCount),
    P: Math.max(0, 2 - punterCount)
  };
};

// ===== VALIDATION UTILITIES =====

export const validateLeagueName = (name: string): string | null => {
  if (!name.trim()) return 'League name is required';
  if (name.length < 3) return 'League name must be at least 3 characters';
  if (name.length > 50) return 'League name must be less than 50 characters';
  return null;
};

export const validateTeamName = (name: string): string | null => {
  if (!name.trim()) return 'Team name is required';
  if (name.length < 2) return 'Team name must be at least 2 characters';
  if (name.length > 30) return 'Team name must be less than 30 characters';
  return null;
};

export const validateEmail = (email: string): string | null => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!email.trim()) return 'Email is required';
  if (!emailRegex.test(email)) return 'Please enter a valid email address';
  return null;
};

// ===== FORMATTING UTILITIES =====

export const formatPlayerName = (player: Player): string => {
  return `${player.name} (${player.position})`;
};

export const formatPlayerWithTeam = (player: Player): string => {
  const nflTeam = player.nflTeam || player.nfl_team;
  const teamAbbr = nflTeam?.abbreviation || 'FA';
  return `${player.name} (${player.position}) - ${teamAbbr}`;
};

export const formatFantasyPoints = (points: number): string => {
  return points.toFixed(1);
};

export const formatRecord = (wins: number, losses: number): string => {
  return `${wins}-${losses}`;
};

export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
};

export const formatDate = (dateString: string): string => {
  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return 'Invalid Date';
  }
};

export const formatDateTime = (dateString: string): string => {
  try {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return 'Invalid Date';
  }
};

// ===== SORTING UTILITIES =====

export const sortPlayersByName = (players: Player[]): Player[] => {
  return [...players].sort((a, b) => a.name.localeCompare(b.name));
};

export const sortPlayersByPosition = (players: Player[]): Player[] => {
  return [...players].sort((a, b) => {
    if (a.position !== b.position) {
      return a.position === 'K' ? -1 : 1; // Kickers first
    }
    return a.name.localeCompare(b.name);
  });
};

// Returns the "current" week to display:
//   - first week with any incomplete matchup (active season)
//   - max completed week if all are done
//   - null if no matchups exist
export const getCurrentWeek = (matchups: Matchup[]): number | null => {
  if (matchups.length === 0) return null;
  const incompleteWeeks = matchups.filter(m => !m.is_completed).map(m => m.week);
  if (incompleteWeeks.length > 0) return Math.min(...incompleteWeeks);
  return Math.max(...matchups.map(m => m.week));
};

export const sortTeamsByStandings = (teams: Team[]): Team[] => {
  return [...teams].sort((a, b) => {
    // Sort by wins descending, then by points for descending
    if (a.wins !== b.wins) {
      return b.wins - a.wins;
    }
    return getTeamField.pointsFor(b) - getTeamField.pointsFor(a);
  });
};

export const sortPlayersByFantasyPoints = (stats: PlayerStats[]): PlayerStats[] => {
  return [...stats].sort((a, b) => getStatsField.fantasyPoints(b) - getStatsField.fantasyPoints(a));
};

// ===== FILTERING UTILITIES =====

export const filterPlayersByPosition = (players: Player[], position: PlayerPosition | 'all'): Player[] => {
  if (position === 'all') return players;
  return players.filter(player => player.position === position);
};

export const filterPlayersByTeam = (players: Player[], teamId: number | null): Player[] => {
  if (!teamId) return players;
  return players.filter(player => {
    const nflTeamId = player.nflTeamId || player.nfl_team_id;
    return nflTeamId === teamId;
  });
};

export const filterPlayersByAvailability = (players: Player[], ownedPlayerIds: number[]): Player[] => {
  return players.filter(player => !ownedPlayerIds.includes(player.id));
};

// ===== CONSTANTS =====

export const ROSTER_LIMITS = {
  KICKERS: 2,
  PUNTERS: 2,
  TOTAL: 4,
} as const;

export const POSITIONS: PlayerPosition[] = ['K', 'P'];

export const ROSTER_POSITIONS = ['K1', 'K2', 'P1', 'P2'] as const;

export const STADIUM_TYPES: StadiumType[] = ['dome', 'retractable', 'outdoor'];

export const SCORING_PRESETS = {
  STANDARD: 'Standard Scoring',
  POWER_LEG: 'Power Leg (Distance Bonus)',
  PRECISION: 'Precision (Accuracy Focus)',
} as const;